import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { invoicesApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { useAppEvent } from "@/utils/appEvents";
import { formatDateOnly } from "@/utils/progress";
import { formatCurrency } from "@/utils/currency";
import {
  invoiceStatusLabels, invoiceStatusBadgeClasses,
} from "@/components/rechnungen/invoiceStatus";
import type { Invoice, InvoiceStatus } from "@/types";
import {
  Search, Plus, Receipt, Loader2, AlertCircle, ChevronRight, Download, Paperclip,
} from "lucide-react";

type StatusFilter = '' | InvoiceStatus;

const statusFilters: Array<{ value: StatusFilter; label: string }> = [
  { value: '', label: 'Alle' },
  { value: 'erfasst', label: 'Erfasst' },
  { value: 'formal_geprueft', label: 'Formal geprüft' },
  { value: 'freigegeben', label: 'Freigegeben' },
  { value: 'korrektur_erforderlich', label: 'Korrektur' },
  { value: 'an_lexware_uebergeben', label: 'An Lexware' },
  { value: 'abgelehnt', label: 'Abgelehnt' },
];

type DirectionFilter = '' | 'eingang' | 'ausgang';

const directionFilters: Array<{ value: DirectionFilter; label: string }> = [
  { value: '', label: 'Alle' },
  { value: 'eingang', label: 'Eingang' },
  { value: 'ausgang', label: 'Ausgang' },
];

/**
 * Rechnungen-Listen-Spalte (Muster BauvorhabenList): eigener
 * Scroll-Container, IntersectionObserver-Nachladen, reloadSeq gegen
 * Races, App-Event-Abo ('rechnungen:changed') für stilles Nachladen.
 */
export function RechnungenList() {
  const { isAdmin } = useAuth();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('eingang');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const reloadSeq = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, directionFilter]);

  useEffect(() => {
    let cancelled = false;
    const seq = ++reloadSeq.current;
    setIsLoading(true);
    setError('');
    invoicesApi
      .getAll({ page: 1, search: debouncedSearch, status: statusFilter, direction: directionFilter })
      .then((response) => {
        if (cancelled || seq !== reloadSeq.current) return;
        setInvoices(response.data.data);
        const pagination = response.data.pagination;
        setHasMore(pagination ? pagination.page < pagination.total_pages : false);
      })
      .catch(() => {
        if (!cancelled) setError('Fehler beim Laden der Rechnungen');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, statusFilter, directionFilter]);

  // Stilles Nachladen der Seiten 1..page (Seite bleibt erhalten) —
  // ausgelöst durch 'rechnungen:changed'.
  const silentReload = useCallback(() => {
    const seq = ++reloadSeq.current;
    const requests = [];
    for (let p = 1; p <= page; p++) {
      requests.push(
        invoicesApi.getAll({ page: p, search: debouncedSearch, status: statusFilter, direction: directionFilter })
      );
    }
    Promise.all(requests)
      .then((responses) => {
        if (seq !== reloadSeq.current) return;
        setInvoices(responses.flatMap((r) => r.data.data));
        const last = responses[responses.length - 1];
        const pagination = last.data.pagination;
        setHasMore(pagination ? pagination.page < pagination.total_pages : false);
      })
      .catch(() => {
        // Still scheitern — die nächste Nutzer-Aktion lädt ohnehin neu.
      });
  }, [page, debouncedSearch, statusFilter, directionFilter]);

  useAppEvent('rechnungen:changed', silentReload);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    invoicesApi
      .getAll({ page: nextPage, search: debouncedSearch, status: statusFilter, direction: directionFilter })
      .then((response) => {
        setInvoices((prev) => [...prev, ...response.data.data]);
        setHasMore((response.data.pagination?.page ?? nextPage) < (response.data.pagination?.total_pages ?? 0));
        setPage(nextPage);
      })
      .catch(() => setError('Fehler beim Laden der Rechnungen'))
      .finally(() => setIsLoadingMore(false));
  }, [isLoadingMore, hasMore, page, debouncedSearch, statusFilter, directionFilter]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !observerTarget.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root, threshold: 1 }
    );
    observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [loadMore]);

  // CSV-Export (Buchhaltung): base64 → Blob → Download (AdminPage-Muster).
  const handleExport = () => {
    setIsExporting(true);
    invoicesApi
      .exportCsv({
        direction: directionFilter || undefined,
        status: statusFilter || undefined,
      })
      .then((response) => {
        const { filename, content_base64 } = response.data.data;
        const binary = atob(content_base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setError('Fehler beim Exportieren'))
      .finally(() => setIsExporting(false));
  };

  return (
    <div ref={scrollRef} className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm px-3 pt-3 pb-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-gray-600" />
            <h1 className="text-lg font-bold text-gray-900">Rechnungen</h1>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                title="CSV-Export für Lexware"
              >
                <Download className="h-4 w-4" />
                {isExporting ? 'Export…' : 'Export'}
              </button>
              <Link to="/rechnungen/new" className="btn btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-sm">
                <Plus className="h-4 w-4" />
                Neu
              </Link>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Suchen — Nummer, Betreff, Lieferant…"
              className="input pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {directionFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setDirectionFilter(filter.value)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  directionFilter === filter.value
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-gray-800 border-gray-800 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-3 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="px-3 pb-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          </div>
        ) : invoices.length === 0 && !error ? (
          <div className="card p-8 text-center text-gray-500">
            <Receipt className="mx-auto mb-3 h-8 w-8 text-gray-400" />
            <p>Keine Rechnungen gefunden</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {invoices.map((inv) => (
              <Link
                key={inv.id}
                to={`/rechnungen/${inv.id}`}
                className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <h2 className="font-semibold text-gray-900 leading-snug truncate">
                      {inv.invoice_number}
                      {inv.has_file && <Paperclip className="ml-1.5 inline h-3.5 w-3.5 text-gray-400" />}
                    </h2>
                    {inv.description && (
                      <p className="truncate text-sm text-gray-500">{inv.description}</p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="font-semibold text-gray-900">{formatCurrency(inv.amount_gross)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-1 text-xs text-gray-500">
                  <span>{inv.direction === 'eingang' ? 'Eingang' : 'Ausgang'}</span>
                  <span>·</span>
                  <span>{formatDateOnly(inv.invoice_date)}</span>
                  {inv.bauvorhaben_name && (
                    <>
                      <span>·</span>
                      <span className="truncate">{inv.bauvorhaben_name}</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="truncate text-sm text-gray-700">{inv.contact_name || '—'}</span>
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${invoiceStatusBadgeClasses[inv.status]}`}
                  >
                    {invoiceStatusLabels[inv.status]}
                  </span>
                  <ChevronRight className="ml-auto h-5 w-5 shrink-0 text-gray-400" />
                </div>
              </Link>
            ))}
          </div>
        )}

        <div ref={observerTarget} className="h-4" />
        {isLoadingMore && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
          </div>
        )}
      </div>
    </div>
  );
}